import React, { useContext, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { CircularProgress, Chip, Typography } from '@mui/material';

import { IconPaperclip, IconSend, IconX } from '@tabler/icons-react';
import { ChatContext } from '@/context/ChatContext';
import { MessageType } from '@/types/apps/chat';
import { useTranslation } from '@/hooks/useTranslation';

interface ChatMsgSentProps {
  ticketId?: string;
  fileId?: string;
}

const ChatMsgSent: React.FC<ChatMsgSentProps> = ({ ticketId, fileId }) => {
  const [msg, setMsg] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const {
    sendMessage,
    sendTicketMessage,
    sendFileMessage,
    selectedChat,
    setSelectedChat,
    setChatContent,
  } = useContext(ChatContext);

  const handleChatMsgChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMsg(e.target.value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const onChatMsgSubmit = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const trimmed = msg.trim();
    const hasContent = Boolean(trimmed) || Boolean(file);

    if (!hasContent) return;
    if (!selectedChat && ticketId !== 'file-service-placeholder' && !fileId) {
      return;
    }

    try {
      setSending(true);
      setError(null);

      if (ticketId === 'file-service-placeholder') {
        const isImage = file ? file.type?.startsWith('image') : false;
        const attachment = file
          ? [
              {
                file: URL.createObjectURL(file),
                fileSize: `${(file.size / 1024).toFixed(1)} KB`,
                icon: file.type,
              },
            ]
          : [];

        const newMessage: MessageType = {
          id: `local-${Date.now()}`,
          msg: trimmed,
          senderId: 'me',
          type: isImage ? 'image' : 'text',
          attachment,
          createdAt: new Date().toISOString(),
        };

        setChatContent(prev => [...prev, newMessage]);
        setSelectedChat(prev =>
          prev
            ? {
                ...prev,
                messages: [...prev.messages, newMessage],
              }
            : prev,
        );
      } else if (fileId) {
        await sendFileMessage(fileId, {
          message: trimmed || undefined,
          file: file || undefined,
        });
      } else if (ticketId) {
        await sendTicketMessage(ticketId, trimmed, file || undefined);
      } else if (selectedChat) {
        await sendMessage(selectedChat.id, trimmed as any);
      }

      setMsg('');
      setFile(null);
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.message || t('chat.errors.failedToSend'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box p={2} sx={{borderTop: '1px solid #E0E0E0', position: 'sticky', bottom: 0}}>
      {error && (
        <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
          {error}
        </Typography>
      )}

      {file && (
        <Box sx={{ mb: 1 }}>
          <Chip
            label={file.name}
            onDelete={handleRemoveFile}
            deleteIcon={<IconX size={16} />}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      <form
        onSubmit={onChatMsgSubmit}
        style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
      >
        <InputBase
          id="msg-sent"
          fullWidth
          value={msg}
          placeholder={t('chat.typeMessage')}
          size="small"
          type="text"
          inputProps={{ 'aria-label': t('chat.typeMessage') }}
          onChange={handleChatMsgChange}
          disabled={sending}
        />

        <IconButton
          component="label"
          aria-label={t('chat.ariaLabels.attachFile')}
          disabled={sending}
        >
          <input
            type="file"
            hidden
            onChange={handleFileChange}
          />
          <IconPaperclip stroke={1.5} size="20" />
        </IconButton>

        <IconButton
          type="submit"
          aria-label={t('chat.ariaLabels.sendMessage')}
          disabled={sending || (!msg.trim() && !file)}
          color="primary"
        >
          {sending ? (
            <CircularProgress size={20} />
          ) : (
            <IconSend stroke={1.5} size="20" />
          )}
        </IconButton>
      </form>
    </Box>
  );
};

export default ChatMsgSent;
